import * as React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import AlertMessageDialog from "../../../components/Dialogs/alertMessageDialog";

/**
 * AssignLoanToPortfolioDialog
 *
 * Props:
 * - open: boolean
 * - loanId: string
 * - portfolios: Array<{ id: string; name: string }>
 * - onClose: () => void
 */
export default function AssignLoanToPortfolioDialog({
  open,
  loanIdOriginal,
  loanId,
  onClose,
}) {
  const [selection, setSelection] = React.useState(null);
  const [portfolios, setPortfolios] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [openAlert, setOpenAlert] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState({
    open: false,
    title: "",
    message: "",
    severity: "info",
  });

  React.useEffect(() => {
    if (!open) setSelection(null);

    if (open) {
      fetchPortfolios();
     
    }
  }, [open]);

  async function fetchPortfolios(){
    try {
      setLoading(true);
      const response = await fetch(process.env.REACT_APP_SERVER_URL+`/api/portafolios/${localStorage.getItem('idcompany')}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany'),
        },
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar los portafolios');
      }
      
      const data = await response.json();
      setPortfolios(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching portfolios:', err);
    } finally {
      setLoading(false);
    }
  }

  const showAlertMessage = ({ title, message, severity = "info" }) => {
    setAlertMessage({ open: true, title, message, severity });
  };

  const handleCloseAlertMessage = () => {
    setAlertMessage((prev) => ({ ...prev, open: false }));
  };

  const handleAssignLoanToPortfolio = async (payload) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/portafolios/${payload.portfolioId}/prestamos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          prestamos_id: [loanIdOriginal],
          portfolioId: payload.portfolioId,
          created_by: localStorage.getItem('id')
        })
      });

      if (!response.ok) {
        throw new Error('Error al asignar el préstamo al portafolio');
      }

      // Show success message
      showAlertMessage({
        title: "Éxito",
        message: "Préstamo asignado correctamente al portafolio",
        severity: "success",
      });
      
      // Close the dialog after success with a delay to show the alert
      setTimeout(() => {
        onClose();
      }, 2000); // 2 second delay
      
    } catch (error) {
      console.error('Error assigning loan to portfolio:', error);
      showAlertMessage({
        title: "Error",
        message: "Error al asignar el préstamo al portafolio. Intente nuevamente.",
        severity: "error",
      });
    }
  };


  const handleAssign = () => {
    if (!selection) return;
    handleAssignLoanToPortfolio({ portfolioId: selection.id });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        Asignar Préstamo a Portafolio
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Préstamo siendo asignado */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Préstamo siendo asignado
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5, border: 1, borderColor: 'divider', bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={700}>ID del Préstamo:</Typography>
              <Chip size="small" label={loanId} color="default" variant="outlined" />
            </Stack>
          </Box>

          {/* Seleccionar Portafolio */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Seleccionar Portafolio
            </Typography>
            <Autocomplete
              value={selection}
              onChange={(_, newValue) => setSelection(newValue)}
              options={portfolios.map(portfolio => ({ id: portfolio.id, name: portfolio.nombre }))}
              getOptionLabel={(opt) => opt?.name ?? ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Buscar un portafolio..."
                  variant="outlined"
                  InputProps={{ ...params.InputProps }}
                />
              )}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5 }}>
        <Button variant="outlined" onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleAssign} disabled={!selection} color="primarybutton" sx={{fontWeight:"bold"}}>
          Asignar
        </Button>
      </DialogActions>
      
      {/* Alert Message Dialog */}
      <AlertMessageDialog
        open={alertMessage.open}
        setOpen={setAlertMessage}
        title={alertMessage.title}
        message={alertMessage.message}
        severity={alertMessage.severity}
        handleClose={handleCloseAlertMessage}
      />
    </Dialog>
  );
}

